import { Set } from 'immutable';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import noop from 'lodash/noop';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { push } from 'react-router-redux';
import { call, fork, put, race, select, take } from 'redux-saga/effects';

import * as PlaybackActions from '../../../actions';
import * as PlaybackConstants from '../../../constants';
import * as StationActions from '../../actions';
import * as StationConstants from '../../constants';
import * as D from '../../deps';
import * as StationServices from '../../services';
import * as StationUtils from '../../utils';

const log = D.Logger.child({ component: 'domains/Station/workers' });
const spawn = D.taskManagerSpawnFactory(StationConstants.DOMAIN_NAME);
let currentSetStationRetryCount = 0;

export function* initialize() {
  const listenerId = yield select(D.Utils.Listener.getCurrentListenerIdFromState);
  if (listenerId) {
    yield call(loadStations, { payload: {} });
  }
}

export function* onLocationChange(action) {
  const isHistoryOpen = yield select(D.Utils.UIState.getIsSessionHistoryOpen);
  if (isHistoryOpen) {
    const nowPlayingPath = yield select(D.Utils.Route.getNowPlayingPath);
    yield put(
      D.Actions.NowPlaying.closeSessionHistory({
        isLeavingNowPlaying: action.payload.pathname !== nowPlayingPath,
      }),
    );
  }
}

export function* buildFragmentRequestData({
  id,
  isNewStation,
  startingTrackToken,
  requestReason = D.Constants.Playback.FragmentRequestReasons.NORMAL,
  isPrefetch,
}) {
  const stationsThatHaveReceivedFragments = yield select(
    StationUtils.getCurrentListenerStationsThatHaveReceivedFragments,
  );
  // Determines if we should get an AAM

  const isStationStart =
    isNewStation ||
    stationsThatHaveReceivedFragments.isEmpty() ||
    !stationsThatHaveReceivedFragments.has(id);

  const onDemandArtistMessageData = yield select(D.Utils.TunerVars.getOnDemandArtistMessageData);
  const { onDemandArtistMessageArtistUidHex = null, onDemandArtistMessageIdHex = null } =
    onDemandArtistMessageData;

  /* clear on demand artist data so it is not replayed */
  yield put(D.Actions.TunerVars.clearOnDemandArtistMessageData());

  // Determine the audio file type:
  // Make this an audio config somewhere
  const audioFormat = yield select(D.Utils.Listener.getCurrentListenerDefaultAudioFormat);
  const getFragmentRequestData = {
    audioFormat,
    fragmentRequestReason: requestReason,
    isPrefetch,
    isStationStart,
    onDemandArtistMessageArtistUidHex,
    onDemandArtistMessageIdHex,
    startingAtTrackId: startingTrackToken,
    stationId: id,
  };
  if (!isStationStart) {
    const lastPlayedTrackToken = yield select(
      D.getInDomain('playback.utils.getLastPlayedTrackTokenOnPlaylist'),
      id,
    );
    const track = yield select(D.Utils.Entity.getTrackById, lastPlayedTrackToken);
    // lastPlayedTrackToken used by the playlist algorithm for track sequencing
    // if the last played track wasn't a music track, but something else (AAM, audio ad) then it shouldn't be passed
    if (track && D.Utils.Playlist.isRegularOrCustomTrack(track)) {
      getFragmentRequestData.lastPlayedTrackToken = lastPlayedTrackToken;
    }
  }

  const testArtistMessage = yield select(D.Utils.TunerVars.getTestArtistMessage);
  const testVoiceTrack = yield select(D.Utils.TunerVars.getTestVoiceTrack);
  if (testArtistMessage) {
    getFragmentRequestData.testArtistMessage = testArtistMessage;
  }
  if (testVoiceTrack) {
    getFragmentRequestData.testVoiceTrack = testVoiceTrack;
  }
  return getFragmentRequestData;
}

export function* setTracksForStation({ data } = {}) {
  const { id, startingTrackToken, requestReason, isPrefetch } = data;
  // Handling changed in PR 2744, getting directly from station
  const isNewStation = yield select(D.getInDomain('playback.utils.getStationIsNew'), id);
  const getFragmentRequestData = yield call(buildFragmentRequestData, {
    id,
    isNewStation,
    isPrefetch,
    requestReason,
    startingTrackToken,
  });
  try {
    const hasPrefetchedTracks = yield select(
      D.getInDomain('playback.utils.hasPrefetchedTracks'),
      id,
    );
    if (
      !isPrefetch &&
      requestReason !== D.Constants.Playback.FragmentRequestReasons.THUMBED_DOWN &&
      requestReason !== D.Constants.Playback.FragmentRequestReasons.CHANGE_MODE &&
      hasPrefetchedTracks
    ) {
      yield call(D.putAndWait, StationActions.loadPrefetchedTracks({ stationId: id }));
      return true;
    }

    const response = yield call(StationServices.getFragment, getFragmentRequestData);
    yield put(D.Actions.Playback.setStationReceivedFragment(id));
    return response.result.tracks;
  } catch (error) {
    if (!(error instanceof D.Errors.ApiError)) {
      // TODO: We should include some error handling here
      return false;
    }

    // If a prefetched getFragment results in an error, we simply ignore it and allow the next non-prefetch getFragment to handle the error.
    if (isPrefetch) return false;

    const { errorTimeStamp, errorType, errorData } = error;
    if (errorType === D.ApiErrorTypes.STREAM_VIOLATION) {
      yield call(
        D.putAndWait,
        D.getInDomain('playback.actions.insertNewSimStreamViolation')({
          ...errorData,
          errorTimeStamp,
        }),
      );
      try {
        yield call(D.putAndWait, D.getInDomain('playback.actions.promptStreamViolation')());

        const newFragmentData = {
          stationId: id,
          audioFormat: getFragmentRequestData.audioFormat,
          isStationStart: getFragmentRequestData.isStationStart,
        };

        const newFragmentResponse = yield call(StationServices.getFragment, newFragmentData);
        yield put(D.Actions.Playback.setStationReceivedFragment(id));
        return newFragmentResponse.result.tracks;
      } catch (err) {
        if (err instanceof D.Errors.TaskManagerError) {
          const simStreamError = err.getFirstErrorOfType(D.Errors.SimStreamError);
          // This means we rejected the stream violation so
          // bubbling up to get handled, otherwise we probably don't know what to do
          // so we'll just 'handle' by returning false
          // eslint-disable-next-line max-depth
          if (simStreamError) {
            throw simStreamError;
          }
        }
        return false;
      }
    } else if (errorType === D.ApiErrorTypes.PLAYLIST_END) {
      yield put(
        D.Actions.Modal.openModalCoachmark(PlaybackConstants.PlaybackMessages.playlistEnded),
      );
      throw error;
    } else {
      // TODO: Error handling
      throw error;
    }
  }
}

/*
 * Return an action for ChronosAdOpportunity to consume.
 * Needs to have a stationId in data property.
 * Sometimes stationId changes in startStation after calling createStation.
 */
export function getChronosAction(
  action,
  stationId,
  suppressAudioAds,
  suppressVideoAds,
  isResumingPlaylistTrack,
) {
  const chronosAction = Object.assign({}, action);

  chronosAction.data = action.data || {};
  chronosAction.data.suppressAudioAds = suppressAudioAds;
  chronosAction.data.suppressVideoAds = suppressVideoAds;
  chronosAction.data.stationId = stationId;
  chronosAction.data.isResumingPlaylistTrack = isResumingPlaylistTrack;

  return chronosAction;
}

/**
 * gets an existing station or creates a new one
 *
 * @param  {string} stationId - the existing station to load if applicable
 * @param  {string} pandoraId - to create station when stationCode not present
 * @param  {object} createStationParams - params object to pass to createStation API call
 * @returns {object} the stationId and whether or not a new station was created
 */

export function* getOrCreateStation(
  { pandoraId, stationId, createStationParams = {} },
  isInitialPlayback,
) {
  // if a pandoraId correlating to a user station is passed in, just return the station's Id
  if (!stationId && pandoraId) {
    let stationCode = yield select(StationUtils.getStationIdFromPandoraId, pandoraId);
    if (!stationCode) {
      stationCode = yield select(StationUtils.getStationIdFromStationFactoryId, pandoraId);
    }
    if (!stationCode) {
      stationCode = yield select(StationUtils.getStationIdFromSeedPandoraId, pandoraId);
    }

    const listenerStation = yield select(StationUtils.getStationByStationId, stationCode, true);
    if (listenerStation) {
      return {
        stationId: listenerStation.get('stationId'),
        newStation: false,
      };
    }
  }

  // if station Id is passed in, check if we have the station,
  // if so, just return the station's Id
  if (stationId) {
    let stationCode = stationId;
    let prefix = StationUtils.getStationIdPrefix(stationCode);
    // Cleave off the station id if we've got a prefix
    if (prefix) {
      stationCode = stationCode.substr(prefix.length);
    } else {
      prefix = D.Constants.Station.StationConstants.SHARED;
    }

    // Check if we've got this station already
    let listenerStation = yield select(StationUtils.getStationByStationId, stationCode, true);
    if (!listenerStation) {
      listenerStation = yield select(StationUtils.getStationByOriginalStationId, stationCode);
    }

    if (!listenerStation) {
      // We just have a station id, not a station code, so create a shared station code
      stationCode = prefix + stationCode;

      createStationParams.stationCode = stationCode;
    } else {
      return {
        stationId: listenerStation.get('stationId'),
        newStation: false,
      };
    }
    // if createStationParams includes musicId, we want to check if we can find an existing
    // station entity rather than attempt to create a new one
  } else if (!stationId && createStationParams.musicId) {
    const musicId = createStationParams.musicId;
    const existingStation = yield select(StationUtils.findStationEntityByMusicId, musicId);
    if (existingStation) {
      return {
        stationId: existingStation.get('stationId'),
        newStation: false,
      };
    }
  }

  let creativeId = null;
  let lineId = null;
  let creationSource = null;
  if (createStationParams.source) {
    const sourceIds = createStationParams.source.split(':');
    if (sourceIds.length === 2) {
      lineId = sourceIds[0];
      creativeId = sourceIds[1];
      creationSource = 'js_api';
    }
  }

  // if a station doesn't have a stationCode we want to use the pandoraId to create one instead
  if (pandoraId && !createStationParams.stationCode) {
    const { result } = yield call(
      StationServices.createStation,
      null,
      null,
      null,
      pandoraId,
      creativeId,
      lineId,
      creationSource,
    );
    const newStation = yield select(StationUtils.getStationIsNew, result);
    return {
      stationId: result,
      newStation,
    };
  }

  // if we are not explicitly passed in a stationId or we don't have the station in the store,
  // create it
  let { stationCode } = createStationParams;
  const {
    musicId,
    stationCodePrefix = null,
    stationName = '',
    searchQuery = '',
  } = createStationParams;

  // generate the stationCode if we aren't provided it
  if (!stationCode) {
    stationCode = StationUtils.generateStationCode(musicId, stationCodePrefix);
  }

  const modeId =
    createStationParams.modeId || isInitialPlayback
      ? yield select(D.Utils.QueryParams.getInteractiveRadioStartupModeId)
      : null;

  // Call createStation service and return result (stationId)
  const { result } = yield call(
    StationServices.createStation,
    stationCode,
    stationName,
    searchQuery,
    null,
    creativeId,
    lineId,
    creationSource,
    modeId,
  );
  const newStation = yield select(StationUtils.getStationIsNew, result);
  return {
    stationId: result,
    newStation,
  };
}

export function* setOrCreateStationHandler({ data, initialPlayback }) {
  // TODO: initialPlaybackShim can be removed with the merge of WEBCA-2505
  const initialPlaybackShim = initialPlayback ?? data?.initialPlayback ?? true;

  const { stationId, newStation } = yield call(getOrCreateStation, data, initialPlaybackShim);
  const startupModeId = yield select(D.Utils.QueryParams.getInteractiveRadioStartupModeId);

  if (!newStation && initialPlaybackShim) {
    if (yield select(StationUtils.shouldSetInteractiveRadioStartupModeId)) {
      yield call(
        D.putAndWait,
        StationActions.setInteractiveRadioMode({
          stationId,
          modeId: startupModeId,
          previousModeId: yield select(StationUtils.getInteractiveRadioCurrentModeId),
        }),
      );
    }
  }

  if (data.createStationParams && data.createStationParams.modeId) {
    yield call(
      D.putAndWait,
      StationActions.setInteractiveRadioMode({
        stationId,
        modeId: data.createStationParams.modeId,
        previousModeId: yield select(StationUtils.getInteractiveRadioCurrentModeId),
      }),
    );
  }

  yield put(
    PlaybackActions.setOrCreateStationFinished({
      data,
      stationId: stationId || data.stationId,
      newStation,
      startupModeId,
    }),
  );

  yield call(setShuffleMode, null);
}

export function* createStation(action) {
  const { pandoraId } = action.data;
  return yield call(getOrCreateStation, { pandoraId });
}

export function* routeToStationDetailsFromFactoryId(action) {
  const result = yield call(createStation, action);

  if (result && result.stationId) {
    const stationDetailsRoute = D.Utils.Route.getStationDetailsPathForStationId(result.stationId);
    yield put(push(stationDetailsRoute));
  }
}

export function* playFirstFromStationListIfPossible(stationList) {
  if (stationList.size > 0) {
    const stationId = stationList.get(0);
    yield call(
      D.putAndWait,
      D.Actions.UseCases.playResumeSource({
        id: stationId,
        pandoraType: D.Constants.SourceTypes.PandoraTypes.USER_STATION,
      }),
    );
  } else {
    yield call(D.putAndWait, D.Actions.UseCases.pauseTrack());
    const myStationsPath = yield select(D.Utils.Route.getMyStationsPath);
    yield put(push(myStationsPath));
  }
}

export function* onStartStationError(errorRecoveryPath, message) {
  if (errorRecoveryPath) {
    // we don't want to play other stations when rerouting for an error
    yield put(push(errorRecoveryPath));
    yield put(
      D.Actions.UseCases.openErrorToast({
        ...PlaybackConstants.PlaybackMessages.startStationFail,
        messageName: D.Constants.Analytics.UserMessages.START_STATION_ERROR,
      }),
    );
    return;
  }

  yield put(
    D.Actions.UseCases.openErrorToast({
      ...message,
      messageName: D.Constants.Analytics.UserMessages.START_STATION_ERROR,
    }),
  );
  const isPlaying = yield select(D.getInDomain('playback.utils.isAudioPlaying'));
  if (!isPlaying && currentSetStationRetryCount > StationConstants.MAX_START_STATION_RETRIES) {
    log.error(
      'onStartStationError(): Station Retry limit reached. This probably is an error where the getFragment call failed',
    );
    currentSetStationRetryCount = 0;
  } else {
    currentSetStationRetryCount++;
    const hasPlaybackStartedInSession = yield select(
      D.getInDomain('playback.utils.hasPlaybackStartedInSession'),
    );
    if (!hasPlaybackStartedInSession) {
      const sortedStationIds = yield select(StationUtils.getRecencySortedStationIds);
      yield spawn(playFirstFromStationListIfPossible, sortedStationIds);
    }
  }
}

export function* loadStationRecommendations() {
  try {
    const response = yield call(StationServices.getStationRecommendations);
    if (!isObject(response)) {
      return null;
    }
    const {
      result: { artists = [], genreStations = [] },
    } = response;
    return artists.length || genreStations.length;
  } catch (error) {
    log.error(
      'loadStationRecommendations(): Error while calling getStationRecommendations: ',
      error,
    );
    return false;
  }
}

export function* getRightsErrorMessage(pandoraId) {
  const annotation = yield select(D.Utils.Entity.getAnnotationsById, pandoraId);
  const pandoraType = annotation && annotation.get('type');
  if (pandoraType === D.Constants.SourceTypes.PandoraTypes.ARTIST) {
    return PlaybackConstants.PlaybackMessages.unavailableArtistStation;
  }
  return PlaybackConstants.PlaybackMessages.unavailableSongStation;
}

export function* startStationErrorHandlers({ data }) {
  const { pandoraId, error, errorRecoveryPath } = data;
  const boundOnStartStationError = onStartStationError.bind(null, errorRecoveryPath);
  if (error instanceof D.Errors.PlaybackError) {
    return yield call(
      boundOnStartStationError,
      PlaybackConstants.PlaybackMessages.weCouldNotStartSelectedStation,
    );
  }
  if (error instanceof D.Errors.RightsError) {
    const rightsErrorMessage = yield call(getRightsErrorMessage, pandoraId);
    return yield call(boundOnStartStationError, rightsErrorMessage);
  }
  if (!(error instanceof D.Errors.TaskManagerError)) throw error;
  if (error.getFirstErrorOfType(D.Errors.ApiError)) {
    const apiError = error.getFirstErrorOfType(D.Errors.ApiError);
    switch (apiError.errorType) {
      case D.ApiErrorTypes.THUMBPRINT_RADIO_NOT_ELIGIBLE:
        return yield call(
          boundOnStartStationError,
          PlaybackConstants.PlaybackMessages.thumbprintRadioIneligible,
        );

      case D.ApiErrorTypes.STATION_LIMIT_REACHED: {
        const isAnon = yield select(D.Utils.Listener.isAnonymousListener);
        const message = isAnon
          ? PlaybackConstants.PlaybackMessages.anonStationLimitReached
          : PlaybackConstants.PlaybackMessages.stationLimitReached;
        return yield call(boundOnStartStationError, message);
      }
      case D.ApiErrorTypes.LISTENER_NOT_AUTHORIZED:
        return yield put(
          D.Actions.UseCases.openErrorToast({
            ...PlaybackConstants.PlaybackMessages.listenerNotAuthorizedForStation,
            messageName: D.Constants.Analytics.UserMessages.LISTENER_NOT_AUTHORIZED,
          }),
        );
      case D.ApiErrorTypes.STATION_DOES_NOT_EXIST:
      case D.ApiErrorTypes.STATION_CODE_INVALID:
      case D.ApiErrorTypes.CONTENT_HAS_EXPIRED:
      case D.ApiErrorTypes.INVALID_REQUEST:
      case D.ApiErrorTypes.SERVER_ERROR: {
        let message = PlaybackConstants.PlaybackMessages.weCouldNotStartSelectedStation;
        if (apiError.errorType === D.ApiErrorTypes.CONTENT_HAS_EXPIRED) {
          message = PlaybackConstants.PlaybackMessages.expiredStation;
        }
        return yield call(boundOnStartStationError, message);
      }
      default:
        log.error('Unhandled station start error');
        throw error;
    }
  } else if (error.getFirstErrorOfType(D.Errors.SimStreamError)) {
    log.trace('User rejected playing the stream', JSON.stringify(error));
    if (yield select(D.Utils.UIState.getIsNowPlayingOpen)) {
      const myStationsPath = yield select(D.Utils.Route.getMyStationsPath);
      yield put(push(myStationsPath));
    }

    // reset the audio player, since we were not able to play any music
    yield put(D.Actions.AudioPlayer.resetAudio());
  } else {
    throw error;
  }
}

// Basically same as skipping track with additional steps before and after
export function* onTrackBecomeTired(action) {
  const { stationId, trackToken } = action.data;
  let index;
  if (yield select(D.getInDomain('playback.utils.isMarkingTrackAsTired'))) {
    // TODO: error handling. swallowed up by tryCall
    throw new D.Errors.ApplicationError(
      D.Constants.Playback.PlaybackErrors.MARKING_TRACK_AS_TIRED_IN_PROGRESS,
    );
  }
  const isPlaybackStillAPS = yield select(D.PlaybackUtils.isPlaylistModelTypeAPSItem);
  if (isPlaybackStillAPS) {
    const apsCurrentItem = yield select(D.getInDomain('playback.selectors.getCurrentItem', null));
    const elapsedTime = yield select(D.getInDomain('playback.selectors.getCurrentItemElapsedTime'));
    index = apsCurrentItem.get('index');
    const deviceUuid = yield select(D.getInDomain('application.utils.getDeviceUuid', ''));
    const data = {
      deviceUuid,
      elapsedTime,
      index,
      sourceId: stationId,
      trackToken: apsCurrentItem.get('trackToken'),
    };
    yield call(StationServices.actionSnooze, data);
  } else {
    yield call(StationServices.addTiredSong, trackToken);
  }
  try {
    yield put(D.getInDomain('analytics.actions.ttmTrackSkippedTransitionFromMusic', noop)());
    yield call(
      D.putAndWait,
      D.Actions.UseCases.skipTrackForward({
        forceNewPlaylist: true,
        index,
        parentAction: action,
        sourceId: stationId,
        type: D.Constants.SourceTypes.SourceTypes.STATION,
      }),
    );
    yield put(D.Actions.Modal.openModalCoachmark(PlaybackConstants.PlaybackMessages.tiredOfTrack));
  } catch (_error) {
    log.error('onTrackBecomeTired(): error while marking track as tired');
  }
}

/**
 * undo previous remove station and if user was listening to that station starts
 * replaying it
 *
 * @param {string} stationId - id of station to undo removal of
 * @param {boolean} wasUserListeningToRemovedStation - if user was previously listening
 */
export function* undoRemoveStation(stationId, wasUserListeningToRemovedStation = false) {
  yield put(D.Actions.Playback.undoRemoveStation(stationId));

  if (wasUserListeningToRemovedStation) {
    yield call(
      D.putAndWait,
      D.Actions.UseCases.playResumeSource({
        id: stationId,
        pandoraType: D.Constants.SourceTypes.PandoraTypes.USER_STATION,
      }),
    );
  }
}

/**
 * confirm removal of current station
 */
export function* confirmRemoveStation() {
  const stationToBeRemoved = yield select(StationUtils.getStationToBeRemoved);

  // we store the station set for removal in the state and do a safety check here because a user can delete multiple
  // stations in quick succession.
  if (stationToBeRemoved) {
    yield call(loadStationRecommendations);
    try {
      const tracksToBeExpired = yield select(
        D.getInDomain('playback.utils.getPlaylistTrackIds'),
        stationToBeRemoved,
      );
      if (tracksToBeExpired) {
        for (let i = 0; i < tracksToBeExpired.size; i++) {
          const trackToken = tracksToBeExpired.get(i);
          const expiredTrack = yield select(D.Utils.Entity.getTrackById, trackToken);
          yield put(
            D.getInDomain('analytics.actions.logTrackEndStation', noop)('expired', expiredTrack),
          );
        }
      }

      // TODO:WEBCA-2365 Clean up once GraphQL migration is stable
      const graphQLMigrationEnabled = yield select(D.Utils.TunerVars.graphQLMigrationEnabled);
      if (graphQLMigrationEnabled) {
        const station = yield select(
          D.getInDomain('playback.utils.getStationByStationId'),
          ...[stationToBeRemoved, false],
        );

        const isHybridStation = D.getInDomain('playback.utils.getIsAdvertiserStation')(station);
        const pandoraId = station.get('pandoraId');
        yield call(
          StationServices.removeStationGraphQL,
          isHybridStation
            ? D.getInDomain('playback.utils.getSourceIdFromStationId')(stationToBeRemoved)
            : pandoraId,
        );
      } else {
        yield call(StationServices.removeStation, stationToBeRemoved);
      }

      yield fork(StationServices.getOrSetShuffleStations);
    } catch (error) {
      if (error instanceof D.Errors.ApiError && error.errorType === D.ApiErrorTypes.READONLY_MODE) {
        const currentStationId = yield select(StationUtils.getCurrentStationId);
        yield call(undoRemoveStation, stationToBeRemoved, currentStationId === stationToBeRemoved);
      }
    } finally {
      yield put(D.Actions.Playback.clearStationToBeRemovedFromServer());
    }
  }
}

/**
 * remove provided station and if station to be removed is the current station
 * switch user to last played station
 *
 * @param  {object} action - redux action
 */
export function* removeStation(action) {
  const { stationId, componentName } = action.data;
  const stationName = yield select(StationUtils.getStationName, stationId);
  const currentStationId = yield select(StationUtils.getCurrentStationId);
  let wasUserListeningToRemovedStation = false;

  // if user already had a station queued up for removal, remove it immediately (circumvent the settimeout that allows user to undo)
  yield call(confirmRemoveStation);

  // if user has elected to remove the station that is now playing; we need to change what is now playing
  // to their last played station (if they have one). if they don't, we need to re-route to my stations page
  if (stationId === currentStationId) {
    wasUserListeningToRemovedStation = true;

    const sortedStationIds = yield select(StationUtils.getRecencySortedStationIds);
    const spliceIndex = sortedStationIds.indexOf(stationId);
    const splicedStationIds = sortedStationIds.splice(spliceIndex, 1);

    // make sure user has another station to play
    yield call(playFirstFromStationListIfPossible, splicedStationIds);
  }

  yield put(D.Actions.Playback.removeStation(stationId));
  yield put(D.Actions.Playback.setStationToBeRemovedFromServer(stationId));
  yield put(D.getInDomain('analytics.actions.logDeleteStation', noop)(stationId, componentName));

  // open up a coachmark that allows user to undo removal intent
  const { dispatch } = D.getStore();

  const modalConfig = {
    modalType: 'ModalCoachMark',
    title: '',

    body: (
      <FormattedMessage
        {...PlaybackConstants.PlaybackMessages.removeStationFromCollectionConfirm}
        values={{ name: stationName }}
      />
    ),
    buttons: [
      <D.Button
        key={0}
        classes={['Button--coachmark--link']}
        onClick={() => {
          dispatch(D.Actions.Modal.closeModal());
          dispatch(
            D.Actions.Playback.undoRemoveStation(stationId, wasUserListeningToRemovedStation),
          );
        }}
        qaTag={D.Constants.QATags.URSB}
      >
        <FormattedMessage {...PlaybackConstants.PlaybackMessages.removeStationUndo} />
      </D.Button>,
    ],

    onClose: () => {
      dispatch(D.Actions.Playback.confirmRemoveStation(stationId));
    },
  };

  yield put(D.Actions.Modal.openModal(modalConfig));
}

export function* trackThumbDown(action) {
  const { trackToken, skip } = action.data;

  const trackBeingThumbedDown = yield select(D.Utils.Entity.getTrackById, trackToken);
  const musicId = trackBeingThumbedDown.get('musicId');
  const stationId = yield select(D.getInDomain('playback.utils.getCurrentPlaylist'));
  const wasTrackThumbedUp = yield call(D.Utils.Playlist.isTrackThumbedUp, trackBeingThumbedDown);
  const relatedTrackTokens = yield select(
    StationUtils.findTrackTokensByMusicIdAndStation,
    musicId,
    stationId,
  );

  try {
    // register track feedback
    log.info('trackThumbDown(): registering thumbdown feedback');
    yield fork(StationServices.addFeedback, {
      isPositive: false,
      musicId,
      relatedTrackTokens,
      trackToken,
      wasTrackThumbedUp,
    });
  } catch (_error) {
    log.error('trackThumbDown(): error while adding feedback');
  }

  // if we aren't skipping we just register feedback and return
  if (!skip) {
    log.info('trackThumbDown(): not skipping track');
    return;
  }

  try {
    yield put(D.getInDomain('analytics.actions.ttmTrackThumbsDownTransitionFromMusic', noop)());
    yield call(
      D.putAndWait,
      D.Actions.UseCases.skipTrackForward({
        forceNewPlaylist: true,
        parentAction: action,
        skipType: D.Constants.Playback.FragmentRequestReasons.THUMBED_DOWN,
        sourceId: stationId,
        type: D.Constants.SourceTypes.SourceTypes.STATION,
      }),
    );
  } catch (_error) {
    log.error('trackThumbDown(): error while thumbing down track');
  }
}

/**
 * Handles the user action TRACK_THUMBUP
 *
 * @param  {object} action - the action being handled
 * @param  {object} store - the redux store
 */
export function* trackThumbUp(action) {
  const { trackToken } = action.data;
  const track = yield select(D.Utils.Entity.getTrackById, trackToken);
  const musicId = track.get('musicId');
  const stationId = track.get('stationId');
  let linkedPlaylist;
  const isAutomaticPlaylistEnabled = yield select(D.TunerVarUtils.isAutomaticPlaylistEnabled);
  const hasPremiumFeatures = yield select(D.Utils.Listener.hasPremiumFeatures);
  if (hasPremiumFeatures && isAutomaticPlaylistEnabled) {
    linkedPlaylist = yield select(
      D.getInDomain('catalog.utils.findLinkedPlaylistFromStationId'),
      stationId,
    );
    const mtuPlaylist = yield select(D.getInDomain('catalog.utils.findMtupPlaylistFromAnnotation'));
    const watchForMtupPlaylistUnlock = yield select(
      D.getInDomain('listener.selectors.watchForMtupPlaylistUnlock'),
    );
    if (!mtuPlaylist) {
      // my thumb up playlist not found so we need to prepare to notify user when its available.
      yield put(D.getInDomain('listener.actions.updateWatchForMtupPlaylistUnlock')(true));
    } else if (mtuPlaylist && watchForMtupPlaylistUnlock) {
      // Time to notify user about MTUP playlist
      yield put(D.getInDomain('listener.actions.updateWatchForMtupPlaylistUnlock')(false));
      yield put(D.getInDomain('listener.actions.updateMtupPlaylistReadyForMinicoachmark')(true));
      yield put(D.Actions.Collection.setCollectionCurrentFilter('all'));
    }
    if (linkedPlaylist && !linkedPlaylist.get('unlocked') && hasPremiumFeatures) {
      yield put(D.Actions.Playlist.unlockPlaylistAndNotify(linkedPlaylist));
    }
  }
  const relatedTrackTokens = yield select(
    StationUtils.findTrackTokensByMusicIdAndStation,
    musicId,
    stationId,
  );
  yield call(StationServices.addFeedback, {
    trackToken,
    isPositive: true,
    relatedTrackTokens,
  });
}

export function* trackRemoveFeedback(isPositive, action) {
  const { trackToken, feedbackId } = action.data;
  let track = yield select(D.Utils.Entity.getTrackById, trackToken);
  track = track || (yield select(D.Utils.Listener.getFeedbackByFeedbackId, feedbackId));

  if (!track) {
    yield put(D.Actions.Modal.openSomethingGoneWrongModal());
    return;
  }

  const stationId = track.get('stationId');
  const musicId = track.get('musicId');
  const relatedTrackTokens = yield select(
    StationUtils.findTrackTokensByMusicIdAndStation,
    musicId,
    stationId,
  );

  yield call(StationServices.deleteFeedback, {
    feedbackId,
    isPositive,
    musicId,
    relatedTrackTokens,
    stationId,
    trackToken,
  });
}

/**
 * Handles the user action TRACK_REMOVE_THUMBDOWN
 *
 * @param  {object} action - the action being handled
 * @param  {object} store - the redux store
 */
export function* trackRemoveThumbDown(action) {
  yield call(trackRemoveFeedback, false, action);
}

/**
 * Handles the user action TRACK_REMOVE_THUMBUP
 *
 * @param  {object} action - the action being handled
 * @param  {object} store - the redux store
 */
export function* trackRemoveThumbUp(action) {
  yield call(trackRemoveFeedback, true, action);
}

function* getShuffleType(shuffleArr) {
  // just using this heuristic to keep things quick
  if (!shuffleArr) return;
  const areAll = yield select(StationUtils.isArraySizeOfShuffleableSet, shuffleArr);
  return areAll ? D.Constants.Shuffle.SHUFFLE_TYPES.ALL : D.Constants.Shuffle.SHUFFLE_TYPES.MY;
}

// Calls the shuffle api with a shuffleList which should be an Array
export function* fetchShuffleStations({ shuffleList, stationId } = {}) {
  try {
    const shuffleType = yield call(getShuffleType, shuffleList);
    yield call(StationServices.getOrSetShuffleStations, shuffleList, shuffleType);
    yield put(
      D.getInDomain(
        'analytics.actions.logShuffleEdit',
        noop,
      )({
        stations: shuffleList.length,
        station_id: +stationId,
        shuffleType,
      }),
    );
  } catch (error) {
    if (error instanceof D.Errors.ApiError) {
      switch (error.errorType) {
        case D.ApiErrorTypes.UNPLAYABLE_SHUFFLE_STATION:
        // TODO: MYSTATIONS_SHUFFLE: should there be some UI to indicate this?
        // case 1: User has no stations, you can't shuffle nothing
        // case 2: User has only 1 station, you can't remove only shuffle station
      }
    }
  }
}

export function* addStationToShuffleWorker(action = {}) {
  let shuffleSet = yield select(StationUtils.getShuffleStationIdSet);
  if (Set.isSet(shuffleSet) && action.data) {
    shuffleSet = shuffleSet.add(action.data.stationToAdd);
    shuffleSet = shuffleSet.toJS() || [];
  }
  yield call(fetchShuffleStations, {
    shuffleList: shuffleSet,
    stationId: action.data.stationToAdd,
  });
}

export function* removeStationFromShuffleWorker(action = {}) {
  let shuffleSet = yield select(StationUtils.getShuffleStationIdSet);
  const isCurrentlyPlayingStationRemoved = yield select(
    StationUtils.isStationShuffling,
    action.data.stationToRemove,
  );
  const shuffleStationId = yield select(StationUtils.getShuffleStationId);
  const currentlyPlayingStation = yield select(StationUtils.getCurrentStationId);

  if (Set.isSet(shuffleSet) && action.data) {
    shuffleSet = shuffleSet.delete(action.data.stationToRemove);
    shuffleSet = shuffleSet.toJS() || [];
  }
  yield call(fetchShuffleStations, {
    shuffleList: shuffleSet,
    stationId: action.data.stationToRemove,
  });
  if (isCurrentlyPlayingStationRemoved && currentlyPlayingStation === shuffleStationId) {
    yield put(
      D.Actions.Playback.playNextTrack({
        sourceId: currentlyPlayingStation,
        sourceType: D.Constants.SourceTypes.SourceTypes.STATION,
        nextTrackForceNewPlaylist: true,
      }),
    );
  }
}

export function* addAllStationsToShuffleWorker() {
  const shuffleableStations = yield select(StationUtils.getShuffleableStationIdsSet);
  const isInShuffleAllMode = yield select(D.getInDomain('playback.utils.isInShuffleAllMode'));
  if (!isInShuffleAllMode) {
    yield put(D.Actions.Playback.toggleShuffleAllStationsMode(true));
  }
  yield call(fetchShuffleStations, { shuffleList: shuffleableStations.toJS() });
}

export function* createStationShuffleWorker() {
  const isInShuffleAllMode = yield select(D.getInDomain('playback.utils.isInShuffleAllMode'));
  if (isInShuffleAllMode) {
    yield call(addAllStationsToShuffleWorker, null);
  }
}

export function* setShuffleMode() {
  const areAllStationsShuffled = yield select(
    D.getInDomain('playback.utils.areAllStationsShuffled'),
  );
  const isInShuffleAllMode = yield select(D.getInDomain('playback.utils.isInShuffleAllMode'));
  if (areAllStationsShuffled !== isInShuffleAllMode) {
    yield put(D.Actions.Playback.toggleShuffleAllStationsMode(areAllStationsShuffled));
  }
}

export function* restorePreviousShuffleWorker() {
  const previousShuffleStations = yield select(StationUtils.getPreviousShuffleSet);
  if (Set.isSet(previousShuffleStations) && previousShuffleStations.size) {
    yield call(fetchShuffleStations, {
      shuffleList: previousShuffleStations.toJS(),
    });
  }
}

export function* removeArtistAudioMessagesFromPlaylist({ payload: { token: trackToken } }) {
  if (!trackToken) {
    return;
  }

  const track = yield select(D.Utils.Entity.getTrackById, trackToken);
  if (!D.Utils.Playlist.isArtistMessage(track)) {
    return;
  }

  const artistAudioMessageTrackTokenSet = yield select(
    D.Utils.Playlist.getArtistAudioMessagesInCurrentPlaylist,
  );
  /* dont include for removal the track token carried in the action, its the track currently playing */
  const artistAudioMessageSet = artistAudioMessageTrackTokenSet.delete(trackToken);
  yield put(D.Actions.Playback.removeArtistAudioMessages(artistAudioMessageSet));
}

export function* clickedArtistAudioMessage(action) {
  const track = action.data.track;
  const currentTrack = track
    ? track
    : yield select(D.getInDomain('playback.utils.getCurrentTrackFromActivePlaylist'));
  // log Artist Audio Message Click
  yield call(
    D.putAndWait,
    D.Actions.Playback.registerArtistMessageMetric(
      currentTrack,
      D.Constants.Amp.AmpMetrics.AUDIO_TILE_CLICK,
    ),
  );
}

export function* clickedVoiceTrack(stationId, track) {
  const currentStationId = stationId ? stationId : yield select(StationUtils.getCurrentStationId);
  const currentTrack = track
    ? track
    : yield select(D.getInDomain('playback.utils.getCurrentTrackFromActivePlaylist'));
  // log Voice Track Click
  yield call(
    D.putAndWait,
    D.Actions.Playback.registerVoiceTrackMetric(
      currentStationId,
      currentTrack,
      D.Constants.Amp.AmpMetrics.AUDIO_TILE_CLICK,
    ),
  );
}

export function* triggeredArtistAudioMessageTile(track) {
  // log Artist Audio Message Tile Impression
  const currentTrack = track
    ? track
    : yield select(D.getInDomain('playback.utils.getCurrentTrackFromActivePlaylist'));
  yield call(
    D.putAndWait,
    D.Actions.Playback.registerArtistMessageMetric(
      currentTrack,
      D.Constants.Amp.AmpMetrics.AUDIO_TILE_IMPRESSION,
    ),
  );
}

export function* triggeredVoiceTrackTile(stationId, track) {
  const currentStationId = stationId ? stationId : yield select(StationUtils.getCurrentStationId);
  const currentTrack = track
    ? track
    : yield select(D.getInDomain('playback.utils.getCurrentTrackFromActivePlaylist'));
  // log Voice Track Tile Impression
  yield call(
    D.putAndWait,
    D.Actions.Playback.registerVoiceTrackMetric(
      currentStationId,
      currentTrack,
      D.Constants.Amp.AmpMetrics.AUDIO_TILE_IMPRESSION,
    ),
  );
}

export function* startTrackPlayback(track, resume) {
  const trackToken = D.Utils.Core.getFromMap(track, 'trackToken');
  const isAudioAd = yield call(D.getInDomain('audioAds.utils.isAudioAd', false), track);
  const shouldTrackStart = !isAudioAd && !resume;

  if (!track || !shouldTrackStart) {
    return;
  }

  try {
    const data = { trackToken };
    const station = yield select(StationUtils.getCurrentStation);
    if (station) {
      data.originalStationId = station.get('originalStationId');
    }
    yield call(D.putAndWait, D.Actions.Playback.logTrackStarted(data));
  } catch (e) {
    if (e instanceof D.Errors.TaskManagerError) {
      const simStreamError = e.getFirstErrorOfType(D.Errors.ApiError);
      // if we get an api error we want to bubble that up
      // since it's probably sim stream
      throw simStreamError;
    } else {
      log.warn('Unable to emit track started call: ', JSON.stringify(e));
    }
  }
}

export function* preStationPlayTrack(action) {
  const { track, resume, dontDispatchPlaybackResumed } = action.data;
  const isAudioAd = yield call(D.getInDomain('audioAds.utils.isAudioAd', false), track);
  const currentStationId = yield select(StationUtils.getCurrentStationId);
  const currentModeId = yield select(StationUtils.getInteractiveRadioCurrentModeId);
  const trackModeId = D.Utils.Core.getFromMap(track, 'modeId');

  if (currentStationId && !isAudioAd && trackModeId !== currentModeId) {
    yield put(
      D.getInDomain('playback.actions.fetchInteractiveRadioModes')({
        stationId: currentStationId,
      }),
    );
  }

  if (!isAudioAd && resume && !dontDispatchPlaybackResumed) {
    yield put(D.Actions.Playback.logTrackResumed());
  }

  try {
    yield call(startTrackPlayback, track, resume);
  } catch (e) {
    if (e instanceof D.Errors.ApiError) {
      const { errorTimeStamp, errorType, errorData } = e;
      if (errorType === D.ApiErrorTypes.STREAM_VIOLATION) {
        yield call(
          D.putAndWait,
          D.getInDomain('playback.actions.insertNewSimStreamViolation')({
            ...errorData,
            errorTimeStamp,
            doUpdate: true,
          }),
        );
      }
    }
  }
}

// Need to separately update feedback data since info like sample src only available back-end
export function* onAddFeedbackSuccess(action) {
  const isNew = yield select(D.Utils.Listener.isNewListener);
  const isAPSEnabled = yield select(D.Utils.Listener.isAPSPandoraStationEnabled);
  const isPositive = isAPSEnabled
    ? action.type === D.Constants.Api.ApiActions.ACTION_THUMB_UP_SUCCESS
    : action.params.isPositive || action.isPositive;
  const sourceId = yield select(StationUtils.getCurrentStationId);
  const isAutoPlay =
    sourceId.includes(D.Constants.SourceTypes.PandoraTypes.AUTOPLAY) ||
    sourceId.includes(D.Constants.SourceTypes.PandoraTypes.PODCAST_AUTOPLAY);

  if (isPositive) {
    if (!(yield select(D.Utils.Listener.hasGivenPositiveFeedback)) && isNew) {
      yield put(
        D.Actions.Modal.openModalCoachmark(
          PlaybackConstants.PlaybackMessages.firstTimeThumbUpEducation,
        ),
      );
      yield put(D.Actions.Listener.setHasGivenFeedback(true));
    }
  } else {
    if (!(yield select(D.Utils.Listener.hasGivenNegativeFeedback)) && isNew) {
      yield put(
        D.Actions.Modal.openModalCoachmark(
          PlaybackConstants.PlaybackMessages.firstTimeThumbDownEducation,
        ),
      );
      yield put(D.Actions.Listener.setHasGivenFeedback(false));
    }
  }

  yield put(
    D.Actions.Api.ApiGetFeedback({
      pageSize: 1,
      startIndex: 0,
      mostRecent: true,
      webname: yield select(D.Utils.Listener.getCurrentListenerWebname),
    }),
  );

  if (!isAutoPlay) {
    return yield put(
      D.Actions.Api.ApiGetStationFeedback({
        mostRecent: true,
        pageSize: 1,
        positive: isPositive,
        startIndex: 0,
        stationId: sourceId,
      }),
    );
  }
}

export function* startNextNonShuffleStation() {
  const stationId = yield select(StationUtils.mostRecentNonShuffleStation);
  if (stationId) {
    yield put(
      D.Actions.UseCases.playResumeSource({
        id: stationId,
        pandoraType: D.Constants.SourceTypes.PandoraTypes.USER_STATION,
      }),
    );
  }
}

export function* loadStations(action) {
  if (
    yield select(D.Utils.Api.isRequestInFlight, {
      endpoint: D.Constants.Api.ApiEndpoints.FETCH_STATIONS,
    })
  ) {
    return;
  }
  const hasStations = yield select(StationUtils.listenerHasStations);

  const fn = hasStations ? spawn : call;
  yield fn(StationServices.getStations, action.payload);
}

export function* fetchStationsSuccess(action) {
  // WEBCA-495 Extra logging for collection showing no stations on login investigation
  const hasStations = yield select(StationUtils.listenerHasStations);
  const totalStationCount = yield select(StationUtils.getTotalStationCount);
  const stations = get(action, 'response.result.stations');
  const totalStations = get(action, 'response.result.totalStations');
  const isValidResponse = Array.isArray(stations) && Number.isInteger(totalStations);
  if (!isValidResponse) {
    log.error(`Invalid response from getStations: ${JSON.stringify(action)}`);
  } else if (isValidResponse && totalStations > 0 && isEmpty(stations)) {
    log.error(`getStations response has empty stations array: ${JSON.stringify(action)}`);
  } else if (totalStations > 0 && (!hasStations || totalStationCount <= 0)) {
    log.error(`getStations response does not match store: ${JSON.stringify(action)}`);
  }
}

// TODO WEBCA-2588: revisit graphql fetching / creating stations. they currently account for old station endpoint response shapes
export function* fetchStationCurators(action) {
  const stations = get(action, 'response.entities.stations', {});
  const stationFactoryPandoraIds = Object.values(stations)
    .map(station => get(station, 'stationFactoryPandoraId', null))
    .filter(stationFactoryPandoraId => !!stationFactoryPandoraId);

  const requestParams = D.GraphQL.Queries.getStationCurators({
    pandoraIds: stationFactoryPandoraIds,
  });
  yield call(D.Services.GraphQL.graphQLRequest(requestParams));
}

export function* getSuggestions(action) {
  yield D.tryCall(StationServices.getSeedSuggestions, action.data);
}

export function* setInteractiveRadioMode({ data: { stationId, modeId, previousModeId } }) {
  const isAPSEnabled = yield select(D.Utils.Listener.isAPSPandoraStationEnabled);
  const sourceId = isAPSEnabled
    ? yield select(StationUtils.getStationIdFromPandoraId, stationId)
    : stationId;
  yield call(StationServices.setInteractiveRadioMode, {
    stationId: sourceId || stationId,
    modeId,
    previousModeId,
  });
}

export function* fetchInteractiveRadioModes(action) {
  const { stationId } = action.data;
  const isAPSEnabled = yield select(D.Utils.Listener.isAPSPandoraStationEnabled);
  const sourceId = isAPSEnabled
    ? yield select(StationUtils.getStationIdFromPandoraId, stationId)
    : stationId;
  yield D.tryCall(StationServices.getInteractiveRadioModes, { stationId: sourceId || stationId });
}

export function* getStationDetails(action) {
  try {
    const isCurrentStation = yield select(StationUtils.isStationCurrent, action.data.stationId);
    const test = yield D.tryCall(StationServices.getStationDetails, {
      stationId: action.data.stationId,
      isCurrentStation,
    });
    if (!test) throw new Error('no results');
  } catch (_err) {
    yield put(push(D.Utils.Route.getNotFoundPath()));
  }
}

export function* getSeeds(action) {
  yield D.tryCall(StationServices.getSeeds, action.data);
}

export function* addSeed(action) {
  const graphQLMigrationEnabled = yield select(D.Utils.TunerVars.graphQLMigrationEnabled);

  if (graphQLMigrationEnabled) {
    const station = yield select(StationUtils.getStationByStationId, action.data.stationId);
    const pandoraId = station.get('pandoraId');
    const seedId = D.Utils.PandoraIdUtil.getPandoraIdFromMusicId(action.data.musicId);

    yield D.tryCall(StationServices.addSeedGraphQL, { stationId: pandoraId, seedId });
  } else {
    yield D.tryCall(StationServices.addSeed, action.data);
  }
}

export function* getSeedInformation(action) {
  const pandoraIds = [];
  const seedsIds = action.response.entities.seeds;
  if (seedsIds) {
    Object.entries(seedsIds).forEach(id => {
      pandoraIds.push(id[1].pandoraId);
    });
  }
  yield put(D.getInDomain('catalog.actions.annotateObjectsSimple')(pandoraIds));
}

export function* addSeedSuccess(action) {
  yield D.tryCall(StationServices.getSeeds, {
    stationId: action.response.result,
  });
}

export function* addSeedSuccessGraphQL(action) {
  const data = JSON.parse(action.params?.content?.variables);
  const stationId = yield select(StationUtils.getStationIdFromPandoraId, data.stationId);

  yield D.tryCall(StationServices.getSeeds, {
    stationId,
  });
}

export function* deleteSeed(action) {
  const graphQLMigrationEnabled = yield select(D.Utils.TunerVars.graphQLMigrationEnabled);

  if (graphQLMigrationEnabled) {
    const station = yield select(StationUtils.getStationByStationId, action.data.stationId);
    const stationId = station.get('pandoraId');
    const seedId = D.Utils.PandoraIdUtil.getPandoraIdFromMusicId(action.data.musicId);

    yield D.tryCall(StationServices.deleteSeedGraphQL, { stationId, seedId });
  } else {
    yield D.tryCall(StationServices.deleteSeed, action.data);
  }
}

export function* deleteSeedSuccessGraphQL(action) {
  const data = JSON.parse(action.params?.content?.variables);
  const stationId = yield select(StationUtils.getStationIdFromPandoraId, data.stationId);

  const initialSeed = yield select(
    D.getInDomain('playback.utils.getInitialSeedByStationId'),
    stationId,
  );
  if (initialSeed === data.musicId) {
    yield D.tryCall(StationServices.getStationDetails, {
      stationId,
    });
  }

  yield D.tryCall(StationServices.getSeeds, {
    stationId,
  });
}

export function* deleteSeedSuccess(action) {
  const initialSeed = yield select(
    D.getInDomain('playback.utils.getInitialSeedByStationId'),
    action.response.result,
  );
  if (initialSeed === action.params.content.musicId) {
    yield D.tryCall(StationServices.getStationDetails, {
      stationId: action.response.result,
    });
  }
  yield D.tryCall(StationServices.getSeeds, {
    stationId: action.response.result,
  });
}

export function* getSeedsSuccess(action) {
  yield D.tryCall(StationServices.getStationDetails, {
    stationId: action.params.content.stationId,
  });
}

export function* fetchStationCurator(action) {
  const station = get(action, 'response.result');
  const requestParams = D.GraphQL.Queries.getStationCurators({
    pandoraIds: [station],
  });
  yield call(D.Services.GraphQL.graphQLRequest(requestParams));
}

export function* deleteFeedbackSuccess(action) {
  const routing = yield select(D.Utils.Route.getRouterCurrentLocation);
  if (D.Utils.Route.isStationBasePath(routing.pathname)) {
    yield put(
      D.Actions.Api.ApiGetStationFeedback({
        stationId: action.stationId,
        positive: action.isPositive,
      }),
    );
  }

  const isStationDetailsPath = D.Utils.Route.isStationDetailsPath(routing.pathname);
  if (isStationDetailsPath) {
    yield D.tryCall(StationServices.getStationDetails, {
      stationId: action.stationId,
    });
  }

  const currentSourceId = yield select(
    D.getInDomain('playback.selectors.getCurrentItemSourceId', ''),
  );
  const annotation = yield select(D.getInDomain('catalog.utils.getAnnotations'), currentSourceId);
  if (
    currentSourceId === D.Utils.SourceType.getStationFeedbackPseudoId(action.stationId) ||
    (annotation && annotation.get('linkedSourceId') === action.stationId)
  ) {
    const currentPandoraId = yield select(
      D.getInDomain('playback.selectors.getCurrentItemPandoraId', ''),
    );
    if (action.response.pandoraId === currentPandoraId) {
      yield put(
        D.getInDomain('playback.actions.playCurrentItemForSource')({
          sourceId: currentSourceId,
        }),
      );
    }
  }
}

export function* addFeedbackSuccess() {
  const routing = yield select(D.Utils.Route.getRouterCurrentLocation);
  const isStationDetailsPath = D.Utils.Route.isStationDetailsPath(routing.pathname);
  if (isStationDetailsPath) {
    const stationId = yield select(D.getInDomain('playback.utils.getStationId'));
    yield D.tryCall(StationServices.getStationDetails, { stationId });
  }
}

export function* getStationFeedbackSuccess(action) {
  const pandoraIds = [];
  const feedbackIds = action.response.entities.feedback;
  if (feedbackIds) {
    Object.entries(feedbackIds).forEach(id => {
      pandoraIds.push(id[1].pandoraId);
    });

    const missingAnnotationTrackIds = yield select(
      D.getInDomain('catalog.utils.getIdsOfMissingAnnotations'),
      pandoraIds,
    );
    if (!missingAnnotationTrackIds.isEmpty()) {
      yield put(
        D.getInDomain('catalog.actions.annotateObjectsSimple')(missingAnnotationTrackIds.toArray()),
      );
    }
  }
}

export function* registerArtistMessageMetric(action) {
  const { track, metric } = action.data;
  const artistMessage = track.get('artistMessage');
  let content = {};
  if (artistMessage) {
    content = {
      artistMessageId: artistMessage.get('artistMessageId'),
      artistUid: artistMessage.get('artistUid'),
      isOnDemand: artistMessage.get('isOnDemand'),
      metric,
      referrer: document && document.referrer ? document.referrer : 'no_referrer', // For some reason the API rejects an empty string referrer
    };
    try {
      yield call(StationServices.insertArtistMessageMetric, content);
    } catch (e) {
      log.warn(`Unable to register Artist Message metric (${metric}): `, JSON.stringify(e));
    }
  }
}

export function* registerVoiceTrackMetric(action) {
  const { stationId, track, statType } = action.data;
  if (D.Utils.VoiceTrack.isStationVoiceTrack(track)) {
    const annotation = yield select(
      D.getInDomain('catalog.utils.getAnnotations'),
      track.get('pandoraId'),
    );
    if (annotation) {
      const content = {
        stationId,
        voiceTrackPandoraId: track.get('pandoraId'),
        authorPandoraId: annotation.get('authorId'),
        statType,
      };

      // all properties are required to prevent API error
      if (
        content.stationId &&
        content.voiceTrackPandoraId &&
        content.authorPandoraId &&
        content.statType
      ) {
        try {
          yield call(StationServices.insertVoiceTrackMetric, content);
        } catch (e) {
          log.warn(`Unable to register Voice Track metric (${statType}): `, JSON.stringify(e));
        }
      } else {
        log.warn(
          `Missing required properties for StationServices.insertVoiceTrackMetric call. Properties given: ${Object.keys(
            content,
          )}`,
        );
      }
    }
  }
}

/*
 * Makes a quick request to the server to see if a HybridStation needs to update its content
 */
export function* playlistNeedsRefresh(stationId) {
  const station = yield select(StationUtils.getStationByStationId, stationId);
  const hybridStation = (station && station.get('hybridStation')) || false;

  // We currently only need to do this for livestreams
  if (hybridStation && hybridStation.get('hasLiveStream')) {
    const checksum = hybridStation.get('checksum');

    try {
      const verifyChecksumResponse = yield call(StationServices.verifyHybridStationChecksum, {
        stationId,
        checksum,
      });
      return verifyChecksumResponse && verifyChecksumResponse.updateNeeded;
    } catch (error) {
      // Maybe the network failed, lets just keep going and hope things go better next time
      log.error('Failed to verify Hybrid Station checksum: ', JSON.stringify(error));
    }
  }

  return false;
}

/**
 * Gets the next playable audio content.
 * This will be the Map for an audio ad if available, or the next track Map in the playlist
 *
 * @param {string} stationId - The station id
 * @param {boolean} force - forces a refresh of the playlist fragment
 * @param {boolean} userInitiatedSkip - is this audio content retrieval because of a user initiated skip?
 * @param {boolean} skipArtistAudioMessage - do not return any artist audio messsages
 * @returns {Function}
 */
// variable used in getNextStationContent for managing retry logic
let callCount = -1;

export function* getNextStationContent({
  id,
  force,
  userInitiatedSkip,
  skipArtistAudioMessageArg,
  skipType,
} = {}) {
  try {
    callCount++;
    let skipArtistAudioMessage = skipArtistAudioMessageArg;
    // only run this on initial call (not on retries)
    if (callCount === 0) {
      const nowPlayingTrack = yield select(D.PlaybackUtils.getPlaylistCurrentTrack, id);
      /* note whether we completed playing an audio ad, we do not play artist audio messages if that is the case */
      skipArtistAudioMessage =
        nowPlayingTrack && D.getInDomain('audioAds.utils.isAudioAd', false)(nowPlayingTrack);

      if (yield select(D.getInDomain('application.utils.idleStatusIsAbandoned'))) {
        // We clear the ad queue when the status becomes abandoned but we also have to do it
        // here because some ads can get fetched in between
        log.debug(
          'Clearing audio ad queue because idle status is "abandoned" to allow getAdList call',
        );
        yield call(D.getInDomain('audioAds.utils.clearAudioAdQueue', null));
      }
    }

    let track;
    const needsRefresh = yield call(playlistNeedsRefresh, id);
    track = yield call(getNextTrack, {
      id,
      force: needsRefresh || force,
      requestReason: skipType,
    });
    // if we don't get a track back, retry getting the next track, but force a refresh
    // of the fragment
    if (!track && callCount < StationConstants.MAX_GET_NEXT_TRACK_RETRIES) {
      track = yield call(getNextStationContent, {
        id,
        force: true,
        userInitiatedSkip,
        skipType,
      });
    }

    if (skipArtistAudioMessage && D.Utils.Playlist.isArtistMessage(track)) {
      track = yield call(getNextStationContent, {
        id,
        force: false,
        userInitiatedSkip: true,
        skipType,
      });
    }

    if (userInitiatedSkip) {
      const artistAudioMessageDeliveryType = yield call(
        D.Utils.Playlist.getArtistMessageDeliveryType,
        track,
      );
      if (
        artistAudioMessageDeliveryType ===
          D.Constants.Amp.ArtistAudioMessageDeliveryTypes.AFTER_TRACK ||
        artistAudioMessageDeliveryType ===
          D.Constants.Amp.ArtistAudioMessageDeliveryTypes.AFTER_OTHER_ARTIST_TRACK
      ) {
        track = yield call(getNextStationContent, {
          id,
          force: false,
          skipType,
        });
      } else {
        const voiceTrackDeliveryType = yield call(
          D.Utils.VoiceTrack.getVoiceTrackDeliveryType,
          track,
        );
        if (voiceTrackDeliveryType === D.Constants.Amp.VoiceTrackDeliveryTypes.AFTER) {
          track = yield call(getNextStationContent, {
            id,
            force: false,
            skipType,
          });
        }
      }
    }
    yield put(D.getInDomain('playback.actions.setCurrentTrackInPlaylist')(id, track));

    if (!track) {
      log.error(`Did not find the track for the current playlist - playlistId: ${id}`);
    }

    return track;
  } catch (error) {
    // if getNextAudiocontent results in stream violation and listener chooses not to hijack the stream
    // do nothing
    if (error instanceof D.Errors.SimStreamError) {
      log.trace('User rejected playing the stream', JSON.stringify(error));
    } else {
      // otherwise rethrow the error
      throw error;
    }
  } finally {
    // eslint-disable-next-line require-atomic-updates
    callCount = -1;
  }
}

export function* getNextTrack({
  id,
  force,
  requestReason = D.Constants.Playback.FragmentRequestReasons.NORMAL,
}) {
  const isPlaylistLastIndex = yield select(D.getInDomain('playback.utils.isLastPlaylistIndex'), id);
  const isPlaylistDone = yield select(D.getInDomain('playback.utils.isPlaylistOutOfTracks'), id);
  const doFetchPlaylist = isPlaylistLastIndex || isPlaylistDone;
  const ad = yield select(D.getInDomain('audioAds.utils.getNextAudioAd', null));

  if (ad) {
    // We want to setup context before we actually play track. Calling from here will make sure we always get context in case of multiple ads
    yield call(D.putAndWait, D.getInDomain('playback.actions.advancePlaylist')(id));
    yield call(D.getInDomain('audioAds.utils.prepareAudioContext'), ad);
    return ad;
  }

  if (doFetchPlaylist || force) {
    yield call(
      D.putAndWait,
      D.getInDomain('playback.actions.stationSetPlaylist')({
        id,
        forceApi: true,
        requestReason,
        type: D.Constants.SourceTypes.SourceTypes.STATION,
      }),
    );
  }

  // advance playlist index
  yield call(D.putAndWait, D.getInDomain('playback.actions.advancePlaylist')(id));
  const updatedPlaylistTracks = yield select(
    D.getInDomain('playback.utils.getPlaylistTrackIds'),
    id,
  );
  if (!updatedPlaylistTracks.size) {
    log.error(`No tracks found for playlist! playlistId: ${id}`);
  }

  const track = yield select(D.getInDomain('playback.utils.getCurrentAudioMapFromPlaylist'), id);

  return track;
}

export function* stationSetPlaylist(action) {
  const { forceApi, id, startingTrackToken, requestReason } = action.data;
  const hasArtistMessage = yield select(D.Utils.TunerVars.hasOnDemandArtistMessageData);
  const shouldForceFetch = hasArtistMessage || forceApi;
  const playlistType = yield select(D.getInDomain('playback.utils.getPlaylistType'), id);
  const isPlaylistAlreadyAvailable = playlistType && !shouldForceFetch && !startingTrackToken;

  yield put(
    D.Actions.Playback.setPlaylist({
      id,
      type: D.Constants.SourceTypes.SourceTypes.STATION,
    }),
  );

  if (!isPlaylistAlreadyAvailable) {
    yield call(D.putAndWait, {
      type: 'SET_TRACKS_FOR_STATION',
      data: {
        id,
        startingTrackToken,
        requestReason,
      },
    });
  }
}

function* logNewTrackStarted(id, track) {
  const playerAction = yield take([
    D.Constants.AudioPlayer.AudioPlayerActions.PLAYING,
    D.Constants.AudioPlayer.AudioPlayerActions.TRACK_END_ERROR,
  ]);
  if (
    playerAction.type === D.Constants.AudioPlayer.AudioPlayerActions.PLAYING &&
    playerAction.payload.token === track?.get('trackToken')
  ) {
    yield put(
      D.Actions.Integrations.newTrackStartedFromSource({
        id,
        track,
        type: D.Constants.SourceTypes.SourceTypes.STATION,
      }),
    );
  }
}

export function* preloadNextTrack({ payload, payload: { token } }) {
  const track = yield select(D.getInDomain('playback.utils.getCurrentTrackFromActivePlaylist'));

  if (D.Utils.Music.isOnDemandIdLike(token) || track?.get('isAdvertisement', false)) {
    // If the token resembles a pandora Id or an ondemand Id, then this isn't a station track token.
    return;
  }

  const isSxmEncryptedStationPreloadEnabled = yield select(
    D.Utils.Listener.isSxmEncryptedStationPreloadEnabled,
  );
  const elapsedTime = payload[D.Constants.AudioPlayer.AudioPlayerKeys.CURRENT_TIME];

  if (isSxmEncryptedStationPreloadEnabled && elapsedTime > 10 && elapsedTime < 16) {
    const playerAction = yield take([
      D.Constants.AudioPlayer.AudioPlayerActions.PLAYING,
      D.Constants.AudioPlayer.AudioPlayerActions.UPDATE,
      D.Constants.AudioPlayer.AudioPlayerActions.TRACK_END_ERROR,
    ]);

    if (
      playerAction.type === D.Constants.AudioPlayer.AudioPlayerActions.PLAYING ||
      playerAction.type === D.Constants.AudioPlayer.AudioPlayerActions.UPDATE
    ) {
      const id = yield select(StationUtils.getCurrentStationId);
      let nextTrack = yield select(D.getInDomain('playback.utils.getNextAudioMapFromPlaylist'), id);
      if (nextTrack) {
        yield put(
          D.Actions.AudioPlayer.preloadTrack({
            track: nextTrack,
          }),
        );
      } else {
        yield call(D.putAndWait, {
          type: 'SET_TRACKS_FOR_STATION',
          data: {
            id,
            requestReason: D.Constants.Playback.FragmentRequestReasons.NORMAL,
            isPrefetch: true,
          },
        });
        nextTrack = yield select(D.getInDomain('playback.utils.getNextAudioMapFromPlaylist'), id);
        if (nextTrack) {
          yield put(
            D.Actions.AudioPlayer.preloadTrack({
              track: nextTrack,
            }),
          );
        }
      }
    }
  }
}

export function* playStation(action = { data: {} }) {
  const { forceNext, id, userTimedOut, seekToInSeconds, userInitiatedSkip, next, skipType } =
    action.data;
  let { nextTrackForceNewPlaylist, starting } = action.data;

  const isInitialLoad = !(yield select(D.PlaybackUtils.hasPlaybackStartedInSession));
  const shouldForceNewPlaylistOnInitialLoad = !starting && isInitialLoad;
  starting = starting || isInitialLoad;

  const shouldPlayNext = yield select(D.getInDomain('playback.utils.shouldPlayNextTrackForId'), id);
  const trackToResume = yield select(D.PlaybackUtils.getPlaylistCurrentTrack, id);
  const getNextContent = (shouldPlayNext && starting) || !trackToResume || forceNext || next;
  let currentTime = 0;

  const isPremium = yield select(D.Utils.Listener.hasPremiumFeatures);
  const interactiveModeId = D.Utils.Core.getFromMap(trackToResume, 'modeId');
  const interactiveMode = yield select(StationUtils.getInteractiveRadioModeById, interactiveModeId);
  const currentlyPlayingTrack = yield select(
    D.getInDomain('playback.utils.getActivelyPlayingTrack'),
  );

  const isCurrentTrackAd = D.getInDomain('audioAds.utils.isAudioAd', false)(currentlyPlayingTrack);

  if (
    getNextContent &&
    (shouldForceNewPlaylistOnInitialLoad ||
      (!isPremium && interactiveMode.get('isPremiumOnly', false)))
  ) {
    nextTrackForceNewPlaylist = true;
  }

  if (isCurrentTrackAd && userInitiatedSkip) {
    yield call(D.getInDomain('audioAds.utils.skipAudioAd'), currentlyPlayingTrack);
  }

  if (id && getNextContent) {
    yield call(getNextStationContent, {
      force: !!nextTrackForceNewPlaylist,
      id,
      skipArtistAudioMessageArg: false,
      skipType, // TODO this should come from skip or thumbed down
      userInitiatedSkip,
    });
  }

  // If we're in the process of ending a PA session because `getFragment`
  // returned a 401, we need to bail out so we don't cut the education message
  // off and cause the PA mediator to stop. It will eventually start the
  // station at the end of it's workflow
  const shouldShortCircuitAfterGetFragment = yield select(
    D.getInDomain('stations.utils.getShouldShortCircuitAfterGetFragment', false),
  );
  if (shouldShortCircuitAfterGetFragment) {
    return;
  }

  let track = yield select(D.PlaybackUtils.getPlaylistCurrentTrack, id);
  currentTime = yield call(D.getInDomain('playback.utils.getCurrentTime'), {
    id,
    next: id && getNextContent,
    starting,
    seekTime: seekToInSeconds,
  });
  const resume = currentTime > 0;

  if (!track) {
    // if we don't get a track, we should resume the currentTrack
    track = yield select(D.getInDomain('playback.utils.getActivelyPlayingTrack'));
  }

  if (userTimedOut) {
    yield call(D.putAndWait, D.getInDomain('ads.utils.tryAdOpportunity', null)(action, track));
  }
  const playTrackData = {
    currentTime,
    dontDispatchPlaybackResumed: action.data.dontDispatchPlaybackResumed,
    isSimStreamViolation:
      (yield select(D.getInDomain('playback.utils.isStreamViolationErrorValid', false))) || false,
    logPlaybackActionsAnalytics: action.data.logPlaybackActionsAnalytics,
    playbackActionsAnalyticsUserInitiated: action.data.playbackActionsAnalyticsUserInitiated,
    playlistId: id,
    resume,
    seekToInSeconds: action.data.seekToInSeconds,
    track,
    userTimedOut,
  };

  if (
    D.getInDomain('audioAds.utils.isAudioAd', false)(track) &&
    resume &&
    !playTrackData.playbackActionsAnalyticsUserInitiated
  ) {
    const content = track.toJS();
    const ctx = {
      ...content,
      stationId: id,
      duration: content.ad_length,
    };
    yield put(D.getInDomain('audioAds.utils.setAudioAnalyticsContext')(ctx));
  }

  try {
    yield call(D.putAndWait, {
      type: D.Constants.Playback.ActionTypes.REQUEST_PLAY_TRACK,
      data: playTrackData,
    });

    yield put(
      D.Actions.AudioPlayer.playTrack({
        track,
        currentTime,
        isInitialLoad,
      }),
    );

    if (!resume) {
      yield spawn(logNewTrackStarted, id, track);
    } else {
      yield put(
        PlaybackActions.stationTrackWillResumeFromSource({
          track,
          type: D.Constants.SourceTypes.SourceTypes.STATION,
          id,
        }),
      );
    }
  } catch (e) {
    if (!(e instanceof D.Errors.TaskManagerError)) throw e;
    if (!e.getFirstErrorOfType(D.Errors.SimStreamError)) {
      // If not a SimStreamError this is unexpected
      throw e;
    }
  }
}

export function* maybeAddStationToCollection({ payload: { pandoraType, pandoraId } }) {
  if (D.Utils.SourceType.isStationType(pandoraType)) {
    yield call(StationServices.createStation, null, null, null, pandoraId);
  }
}

export function* maybeRemoveStationFromCollection({ payload }) {
  if (D.Utils.SourceType.isStationType(payload.pandoraType)) {
    let { stationId } = payload;
    if (!stationId && payload.pandoraId) {
      const station = yield select(StationUtils.getMyStationByFactoryPandoraId, payload.pandoraId);
      // eslint-disable-next-line require-atomic-updates
      stationId = station && station.get('stationId');
    }
    const station = yield select(StationUtils.getStationByStationId, stationId, true);
    if (station) {
      const { dispatch } = D.getStore();
      const { redirectToMyMusic, componentName } = payload;
      const modalParams = {
        hideXRow: true,
        modalType: 'ModalStandard',
        title: <FormattedMessage {...StationConstants.messages.removeStationTitle} />,

        body: (
          <FormattedMessage
            {...StationConstants.messages.removeStationBody}
            values={{ stationName: StationUtils.getName(station) }}
          />
        ),
        buttons: [
          <D.ButtonT3
            key={0}
            theme={D.ButtonT3Theme.LIGHT_TRANSPARENT}
            isInline={true}
            onClick={() => {
              dispatch(D.Actions.Modal.closeModal());
            }}
          >
            <FormattedMessage {...StationConstants.messages.cancel} />
          </D.ButtonT3>,
          <D.ButtonT3
            key={1}
            isInline={true}
            onClick={() => {
              dispatch(D.Actions.Modal.closeModal());
              dispatch(
                D.Actions.Collection.confirmRemoveStationFromCollection({
                  stationId,
                  redirectToMyMusic,
                  componentName,
                }),
              );
            }}
          >
            <FormattedMessage {...StationConstants.messages.ok} />
          </D.ButtonT3>,
        ],

        onClose: () => {
          dispatch(D.Actions.Modal.closeModal());
        },
      };
      yield put(D.Actions.Modal.openModal(modalParams));
    }
  }
}

export function* confirmRemoveStationFromCollection({
  payload: { stationId, componentName, redirectToMyMusic },
}) {
  if (stationId) {
    yield put(D.Actions.Playback.startRemoveStationFlow(stationId, componentName));
    if (redirectToMyMusic) {
      yield put(push(D.Utils.Route.getMyMusicPath()));
    }
  }
}

export function* onGetStationFeedback(action) {
  const data = action.data;
  yield call(StationServices.getStationFeedback, data);
}

// Adapters

/*
 * Worker saga that listens for the action indicating that a track has started playing
 */
export function* addToSessionHistoryUponTrackLoad({ data }) {
  const currentStationId = yield select(D.getInDomain('playback.utils.getCurrentStationId'));
  const { audioErrorAction } = yield race({
    playAction: take(D.Constants.AudioPlayer.AudioPlayerActions.PLAYING),
    audioErrorAction: take(D.Constants.AudioPlayer.AudioPlayerActions.ERROR),
  });
  if (audioErrorAction) {
    // when autoplay is blocked, add track to session history to avoid
    // album spinner for a better experience
    const errorType = audioErrorAction.payload.ERROR_OBJ.commonType;
    if (errorType !== D.Constants.AudioPlayer.CommonAudioErrorCodes.BROWSER_AUTOPLAY_BLOCKED) {
      return;
    }
  }
  yield put({
    type: D.Constants.Playback.ActionTypes.ADD_TRACK_TO_STATION_HISTORY,
    data: {
      ...data,
      stationId: currentStationId,
    },
  });
}

// function* watchPlayNextTrack() {
//   while (LOOP) {
//     const action = yield take(D.Constants.Playback.ActionTypes.PLAY_NEXT_TRACK);
//     yield call(D.putAndWait, D.getInDomain('playback.actions.playTrackFromStation')({
//       id: action.sourceId
//     }));
//   }
// }

export function* handlePlayNextTrack(action) {
  yield call(
    D.putAndWait,
    D.getInDomain('playback.actions.playTrackFromStation')({
      id: action.sourceId,
    }),
  );
}

/*
 * Watch for PLAY_TRACK action and execute blocking onPlayTrack workflow
 */
// TODO: This mainly just station specific logic, refactor so this is more spare for
// other sources, which just want the ability to be notified of playback interception
export function* requestPlayTrack(action) {
  const { data } = action;

  if (yield select(D.getInDomain('playback.utils.isStreamViolationValidAndNotStale', false))) {
    throw new D.Errors.SimStreamError('There is an active stream violation, cannot play');
  }

  const isPremium = yield select(D.Utils.Listener.hasPremiumFeatures);
  if (isPremium && data.track && data.track.get) {
    const id = data.track.get('pandoraId');
    if (id) {
      yield put(D.getInDomain('catalog.actions.annotateObjectsSimple')([id]));
    }
  }

  // If audio ad send the listener back to the now playing page if they are in session history
  const isSessionHistoryOpen = yield select(D.Utils.UIState.getIsSessionHistoryOpen);

  if (D.getInDomain('audioAds.utils.isAudioAd', false)(action.data.track) && isSessionHistoryOpen) {
    const currentStationId = yield select(D.getInDomain('playback.utils.getCurrentStationId'));
    yield put(push(D.Utils.Route.getStationPlayPathForStationCode(currentStationId)));
  }

  // We will listen for the start of playback, which could occur somewhere in or after the onPlayTrack workflow
  // which handles loading a source into the player, starting playback, and calling analytics services.
  yield put({ type: 'START_ADDING_TO_SESSION_HISTORY', data: action.data });
}

export function* handleRemoveStation(action) {
  const isAdvertiser = yield select(D.Utils.Listener.isListenerAdvertiser);
  if (isAdvertiser) {
    yield put(
      D.Actions.Modal.openModalCoachmark(
        D.Constants.ServiceErrors.ServiceErrorMessages.advertiserAccountDeletionWarning,
      ),
    );
  } else {
    yield D.tryCall(removeStation, action);
  }
}

export function* handleRemoveArtistAudioMessagesFromPlaylist(action) {
  const resume = action.payload.resume;
  if (!resume) {
    yield D.tryCall(removeArtistAudioMessagesFromPlaylist, action);
  }
}

export function* handleClickedVoiceTrack(action) {
  yield D.tryCall(clickedVoiceTrack, action.data.stationId, action.data.track);
}

export function* handleTriggeredArtistAudioMessageTile(action) {
  yield D.tryCall(triggeredArtistAudioMessageTile, action.data.track);
}

export function* handleTriggeredVoiceTrackTile(action) {
  yield D.tryCall(triggeredVoiceTrackTile, action.data.stationId, action.data.track);
}

export function* openDiscoveryTunerModalOnInitialPlay({ data: { forceApi, startupModeId } }) {
  take(D.Constants.Integrations.ActionTypes.TRACK_STARTED_FROM_SOURCE);
  if (forceApi && startupModeId) {
    const mode = yield select(
      D.getInDomain('playback.utils.getInteractiveRadioModeById', null),
      startupModeId,
    );
    const name = mode && mode.get('modeName');
    const description = mode && mode.get('modeDescription');
    if (name && description) {
      const store = D.getStore();
      yield put(
        D.Actions.Modal.openModal({
          modalType: 'ModalCoachMark',
          body: (
            <div>
              <div>
                <strong>{name}</strong>
              </div>
              {description}
            </div>
          ),
          onClose: () => {
            store.dispatch(D.Actions.Modal.closeModal());
          },
        }),
      );
    }
  }
}
